//
// # Arc Entity
//

import { model, graphics } from "GenAlphaModel";
import { EntityKind } from "../entity";
import { MousePos } from "../mousepos";
import { DrawTo } from "../draw";
import { ShapeBase } from "../tree";

// How close (in schematic units) the cursor must be to the arc curve to hit it.
const CURVE_HIT_TOLERANCE = 6;

export class ArcEntity extends ShapeBase<graphics.Arc> {
  readonly entityKind = EntityKind.Arc;

  override redraw = (to: DrawTo): unknown => {
    return to.arc(this.data, this.style);
  };

  // Current center of the arc (derived from the ellipse bbox).
  get loc(): model.Point {
    return model.Bbox.sizeAndCenter(this.data.ellipseBbox).center;
  }

  // The four ellipse-bbox corners, as draggable resize handles.
  // Resizing reshapes the underlying ellipse; the start/stop angles are held
  // fixed (so the visible sweep is preserved) and arcBbox is recomputed.
  get corners(): [ArcCorner, ArcCorner, ArcCorner, ArcCorner] {
    const { ellipseBbox } = this.data;
    const opposites = [
      [ellipseBbox.min, ellipseBbox.max],
      [
        { x: ellipseBbox.min.x, y: ellipseBbox.max.y },
        { x: ellipseBbox.max.x, y: ellipseBbox.min.y },
      ],
    ];
    return [
      new ArcCorner(this, opposites[0][0], opposites[0][1]),
      new ArcCorner(this, opposites[0][1], opposites[0][0]),
      new ArcCorner(this, opposites[1][0], opposites[1][1]),
      new ArcCorner(this, opposites[1][1], opposites[1][0]),
    ];
  }

  // Translate the arc to a new center location.
  updateLoc = (loc: model.Point): unknown => {
    const oldCenter = this.loc;
    const delta = model.Point.sub(loc, oldCenter);
    return this.update({
      ellipseBbox: model.Bbox.translate(this.data.ellipseBbox, delta),
      arcBbox: model.Bbox.translate(this.data.arcBbox, delta),
    });
  };

  // Reshape the ellipse from a new bbox, keeping the angles fixed.
  // arcBbox just tracks the ellipse bbox (the arc is always within it).
  updateEllipseBbox = (ellipseBbox: model.Bbox): unknown => {
    return this.update({
      ellipseBbox,
      arcBbox: model.Bbox.clone(ellipseBbox),
    });
  };

  // Hit test against the arc's ellipse ring (for whole-shape movement).
  // We test the full ellipse rather than just the visible segment — simple,
  // and the whole shape is what you grab to move it.
  hitTest = (mousePos: MousePos): boolean => {
    const { ellipseBbox } = this.data;
    const center = model.Bbox.center(ellipseBbox);
    const { width, height } = model.Bbox.size(ellipseBbox);
    const rx = width / 2;
    const ry = height / 2;
    if (rx <= 0 || ry <= 0) return false;

    const m = mousePos.sch;
    const angle = Math.atan2(m.y - center.y, m.x - center.x);
    // Closest point on the ellipse at the cursor's angle.
    const ring = {
      x: center.x + rx * Math.cos(angle),
      y: center.y + ry * Math.sin(angle),
    };
    return model.Point.dist(m, ring) < CURVE_HIT_TOLERANCE;
  };
}

// A draggable corner handle that resizes the arc's ellipse bbox.
// The start/stop angles are fixed for the arc's lifetime, so dragging only
// changes the ellipse's size/shape; arcBbox is recomputed via the parent.
export class ArcCorner {
  readonly entityKind = EntityKind.ArcCorner;

  constructor(
    public parent: ArcEntity,
    public loc: model.Point,
    public opposite: model.Point
  ) {}

  hitTest = (mousePos: MousePos): boolean => {
    return model.Point.dist(mousePos.sch, this.loc) < 5;
  };

  update = (data: { loc: model.Point }) => {
    const { loc } = data;
    this.parent.updateEllipseBbox(model.Bbox.fromPoints(loc, this.opposite));
    this.loc = loc;
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
  highlight = (state: boolean) => this.parent.highlight(state);
}
